import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Toggle } from '@/components/ui/Toggle'
import { useCanReadFinance } from '@/lib/auth'
import { cn } from '@/lib/utils'
import type { HoursReportClient, HoursReportProject } from '@/lib/report-types'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
}

function RecipientCount({ count }: { count: number }) {
  if (count === 0) return <span className="text-ink-faint">Geen</span>
  return count
}

function NewLinesPill({ count, provisional }: { count: number; provisional: number }) {
  return (
    <>
      <span
        className={cn(
          'num rounded-pill px-2.5 py-1 text-xs font-medium',
          count > 0 ? 'bg-status-approval/10 text-status-approval' : 'bg-status-paid/10 text-status-paid',
        )}
      >
        {count}
      </span>
      {provisional > 0 ? <span className="ml-2 text-xs text-ink-faint">{provisional} als concept</span> : null}
    </>
  )
}

export function ReportClientsTable({
  clients,
  onView,
  onToggleWeekly,
  togglingId,
}: {
  clients: HoursReportClient[]
  onView: (client: HoursReportClient, project: HoursReportProject) => void
  onToggleWeekly: (client: HoursReportClient, next: boolean) => void
  togglingId: string | null
}) {
  const canEditSettings = useCanReadFinance()
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())

  function toggleExpanded(clientId: string) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(clientId)) next.delete(clientId)
      else next.add(clientId)
      return next
    })
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-hairline">
            <th className="label-caps px-3 py-2 text-left text-ink-faint">Klant / project</th>
            <th className="label-caps px-3 py-2 text-center text-ink-faint">Wekelijks</th>
            <th className="label-caps px-3 py-2 text-left text-ink-faint">Ontvangers</th>
            <th className="label-caps px-3 py-2 text-left text-ink-faint">Laatst verzonden</th>
            <th className="label-caps px-3 py-2 text-left text-ink-faint">Nieuwe regels</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {clients.map((client) => {
            const noRecipients = client.recipients.length === 0 && client.projects.every((p) => p.recipients.length === 0)
            const multiProject = client.projects.length > 1
            const perProject = multiProject && client.projects.some((p) => p.recipientsSource === 'project')
            const isOpen = expanded.has(client.clientId)
            const singleProject = client.projects.length === 1 ? client.projects[0] : null
            return [
              <tr key={client.clientId} className="align-middle">
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-2 font-medium text-ink">
                    {multiProject ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(client.clientId)}
                        aria-label={isOpen ? 'Projecten verbergen' : 'Projecten tonen'}
                        className="flex h-5 w-5 items-center justify-center rounded-sm text-ink-faint hover:bg-fill-2 hover:text-ink"
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    ) : (
                      <span className="h-5 w-5" />
                    )}
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: client.brandColor }} />
                    {client.clientName}
                    {multiProject ? <span className="text-xs font-normal text-ink-faint">{client.projects.length} projecten</span> : null}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-center">
                    <Toggle
                      checked={client.weeklyEnabled}
                      label="Wekelijkse verzending"
                      disabled={!canEditSettings || noRecipients || togglingId === client.clientId}
                      title={noRecipients ? 'Voeg eerst een ontvanger toe' : undefined}
                      onChange={(next) => onToggleWeekly(client, next)}
                    />
                  </div>
                </td>
                <td className="px-3 py-2.5 text-ink-soft">
                  {perProject ? <span className="text-ink-faint">Per project</span> : <RecipientCount count={client.recipients.length} />}
                </td>
                <td className="px-3 py-2.5 text-ink-soft">
                  {client.lastSentAt ? formatDate(client.lastSentAt) : <span className="text-ink-faint">Nog niet</span>}
                </td>
                <td className="px-3 py-2.5">
                  <NewLinesPill count={client.newLinesSince} provisional={client.provisionalCount} />
                </td>
                <td className="px-3 py-2.5 text-right">
                  {client.projects.length > 0 ? (
                    <button
                      type="button"
                      className="btn-secondary h-8 px-3 text-sm"
                      onClick={() => onView(client, singleProject ?? client.projects[0])}
                    >
                      Bekijk
                    </button>
                  ) : null}
                </td>
              </tr>,
              ...(multiProject && isOpen
                ? client.projects.map((project) => (
                    <tr key={`${client.clientId}-${project.projectId}`} className="bg-fill-1/50 align-middle">
                      <td className="py-2.5 pl-14 pr-3 text-ink-soft">{project.projectName}</td>
                      <td className="px-3 py-2.5 text-center">
                        {project.reportEnabled ? null : <span className="text-xs text-ink-faint">Rapport uit</span>}
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">
                        <RecipientCount count={project.recipients.length} />
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">
                        {project.lastSentAt ? formatDate(project.lastSentAt) : <span className="text-ink-faint">Nog niet</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <NewLinesPill count={project.newLinesSince} provisional={project.provisionalCount} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button type="button" className="btn-secondary h-8 px-3 text-sm" onClick={() => onView(client, project)}>
                          Bekijk
                        </button>
                      </td>
                    </tr>
                  ))
                : []),
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}
